import { format, isAfter, parse, subDays } from 'date-fns'
import { FormikErrors, FormikProvider, useFormik, useFormikContext } from 'formik'
import { ElementType, FC, Fragment, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { CircleCloseIcon } from '@icons/CircleCloseIcon'
import { IconProps } from '@icons/IconProps'
import { NavigationArrowDownIcon } from '@icons/NavigationArrowDownIcon'

import { Caption1 } from '@ui/Typography/Typography'

import { Operator } from '@utils/ApiClient'
import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { Badge } from '@global/badge/Badge'
import { Button } from '@global/button/Button'
import { FilterQueryProps } from '@global/filter/FilterQueryModal'
import { Modal } from '@global/modal/Modal'

import { DateInput } from '../../DateInput/DateInput'
import { ModalContentWrapper } from '../../Modal/ModalContentWrapper'
import { RangeWrapper } from './components/RangeWrapper'
import { FromTo } from './interfaces/fromTo.interface'

import styles from './FilterChipModalDateRange.module.scss'

export interface DateShortcutOption {
  name: string
  range: [Date, Date]
}

interface FilterChipModalDateRangeProps {
  placeholders?: FromTo
  name: string
  keys: FromTo
  state?: FilterQueryProps
  isQueryParameter?: boolean
  shortcutOptions?: DateShortcutOption[]
  onSelect?: (value: FilterQueryProps) => void
  inputIcon?: ElementType<IconProps>
}

const DATE_FORMAT = 'MM/dd/yyyy'

export const FilterChipModalDateRange: FC<FilterChipModalDateRangeProps> = (props) => {
  const { t } = useTranslation()

  const { placeholders, name, keys, state, isQueryParameter, shortcutOptions, onSelect } = props

  const [auth] = useContext(AuthSessionContext)

  const dateFormat = auth?.dateFormatType?.name

  const [modalOpen, setModalOpen] = useState(false)

  const getValueBasedOnQueryParameter = (operator: Operator, value?: Date) => {
    if (!value || !dateFormat) {
      return undefined
    }

    const formattedValue = format(value, DATE_FORMAT)

    return isQueryParameter
      ? formattedValue
      : {
          value: formattedValue,
          operator: operator,
        }
  }

  const handleSelect = (values: FromTo<Date | undefined>) => {
    onSelect?.({
      ...state,
      [keys.from]: getValueBasedOnQueryParameter(Operator.MORE_OR_EQUAL, values.from),
      [keys.to]: getValueBasedOnQueryParameter(Operator.LESS_OR_EQUAL, values.to),
    })

    handleClose()
  }

  const parseDate = (date?: string) => {
    if (!dateFormat || !date?.length) {
      return undefined
    }

    return parse(extractValueFromFilterQuery(date), DATE_FORMAT, new Date())
  }

  const formik = useFormik<FromTo<Date | undefined>>({
    initialValues: {
      from: parseDate(state?.[keys.from] as string),
      to: parseDate(state?.[keys.to] as string),
    },
    onSubmit: (values, { resetForm }) => {
      handleSelect(values)
      resetForm()
    },
    validate: (values) => {
      const errors: FormikErrors<FromTo> = {}

      if (values.from && values.to && isAfter(values.from, values.to)) {
        errors.to = t('Validation.To date should be greater than from date')
      }

      return errors
    },
    validateOnChange: false,
    enableReinitialize: true,
  })

  const { resetForm } = formik

  const handleClose = () => {
    setModalOpen(false)
    resetForm()
  }

  return (
    <>
      <Button.New
        renderRightIcon={() => <NavigationArrowDownIcon />}
        appearance='secondary'
        size='XS'
        onClick={() => setModalOpen(true)}
      >
        <div className={styles.filterChip}>
          {name}
          {(state?.[keys.from] || state?.[keys.to]) && (
            <Badge.New
              key={`${keys.from}-${keys.to}`}
              text={state?.[keys.from] && state?.[keys.to] ? '2' : '1'}
              color='blue'
              size='small'
              rightIcon={CircleCloseIcon}
              onRightIconClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                onSelect?.({
                  ...state,
                  [keys.from]: undefined,
                  [keys.to]: undefined,
                })
              }}
            />
          )}
        </div>
      </Button.New>
      {modalOpen && (
        <FormikProvider value={formik}>
          <Modal
            pullDown
            closeModal={handleClose}
            render={() => (
              <ModalContent
                name={name}
                placeholders={placeholders}
                handleClose={handleClose}
                shortcutOptions={shortcutOptions}
              />
            )}
          />
        </FormikProvider>
      )}
    </>
  )
}

interface ModalContentProps {
  name: string
  placeholders?: FromTo
  shortcutOptions?: DateShortcutOption[]
  handleClose: () => void
}

const ModalContent: FC<ModalContentProps> = (props) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const {
    name,
    placeholders = { from: t('Calendar.Start date'), to: t('Calendar.End date') },
    shortcutOptions,
    handleClose,
  } = props

  const { setFieldValue, errors, values, handleSubmit, setErrors } =
    useFormikContext<FromTo<Date | undefined>>()

  const handleChange = (key: string, value?: Date) => {
    setFieldValue(key, value)

    setErrors({
      ...errors,
      [key]: undefined,
    })
  }

  const dateFormat = auth?.dateFormatType?.name ?? DATE_FORMAT
  const parsedDateFormat = dateFormat.includes(' ') ? dateFormat.split(' ')[0] : dateFormat
  const yesterday = subDays(new Date(), 1)

  const isDisabled = !!errors.from || !!errors.to || (!values.from && !values.to)

  return (
    <ModalContentWrapper
      title={name}
      isApplyDisabled={isDisabled}
      onClose={handleClose}
      onConfirm={handleSubmit}
      onCancel={handleClose}
    >
      <RangeWrapper
        leftInput={
          <DateInput
            value={values.from}
            placeholder={placeholders.from}
            onChange={(value) => handleChange('from', value)}
            hint={errors.to}
            dateFormat={parsedDateFormat}
            error={!!errors.to}
            maxDate={yesterday}
          />
        }
        rightInput={
          <DateInput
            value={values.to}
            placeholder={placeholders.to}
            onChange={(value) => handleChange('to', value)}
            hint={errors.to}
            dateFormat={parsedDateFormat}
            error={!!errors.to}
            maxDate={yesterday}
          />
        }
      />
      {!!shortcutOptions?.length && (
        <div className={styles.shortcutOptions}>
          {shortcutOptions.map((option) => (
            <Fragment key={option.name}>
              <div className={styles.shortcutOptionDivider} />

              <Caption1
                className={styles.shortcutOption}
                onClick={() => {
                  handleChange('from', option.range[0])
                  handleChange('to', option.range[1])
                }}
              >
                {option.name}
              </Caption1>
            </Fragment>
          ))}
        </div>
      )}
    </ModalContentWrapper>
  )
}
